/**
 * V1.5 Command Palette — ⌘K / Strg+K.
 * Quelle: Implementation Brief §7.10 + Screen-Inventory §1.5.
 *
 * Phase 2: Stub-Variante mit Navigation + Theme-Switch + Sprache wechseln.
 * Volltext-Suche über Projekte/Aufträge/User folgt in Phase 3+.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { useSession } from '../../lib/session.js';
import { resolveStaffingNavCommand } from './command-palette-nav.js';
import { formatProjectSearchLabel } from './command-palette-search.js';
import { Modal } from './modal.js';
import { useTheme } from './theme-provider.js';

type Command = {
  id: string;
  group: string;
  label: string;
  hint?: string;
  run: () => void;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  // Seit PV-016 liegt /staffing auf der flag-gegateten neuen Sicht — ohne Flag liefe der
  // Eintrag auf `Navigate to="/"` und damit ins Leere. Gleicher Gate wie in der Sidebar.
  const showStaffingNeu = useFeatureFlag('staffing_neu');
  // Der Eintrag haengt an der FUNKTIONSROLLE, nicht an der Persona — siehe sidebar.tsx
  // (Z. ~223-226) fuer die Begruendung. Die Palette spiegelt exakt dieselbe Logik (#36).
  const isKfml = (user?.function_roles ?? []).some((fr) => fr.role === 'kaufm_leitung');
  const staffingNavCommand = useMemo(
    () => resolveStaffingNavCommand(isKfml, showStaffingNeu),
    [isKfml, showStaffingNeu],
  );
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce für die serverseitige Projektsuche (F10).
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 180);
    return () => clearTimeout(id);
  }, [query]);

  const searchQuery = useQuery({
    queryKey: ['cmdk-search', debounced],
    queryFn: () => api.searchProjects(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });
  const groupProjects = t('v2.cmdk.group_projects', 'Projekte') as Command['group'];

  const groupNav = t('v2.cmdk.group_navigation', 'Navigation') as Command['group'];
  const groupAct = t('v2.cmdk.group_actions', 'Aktionen') as Command['group'];
  const groupSet = t('v2.cmdk.group_settings', 'Einstellungen') as Command['group'];

  const commands: Command[] = useMemo(
    () => [
      {
        id: 'nav-home',
        group: groupNav,
        label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
          label: t('v2.nav.mission_control', 'Mission Control'),
        }),
        run: () => navigate('/'),
      },
      {
        id: 'nav-atlas',
        group: groupNav,
        label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
          label: t('v2.nav.atlas', 'Portfolio Atlas'),
        }),
        run: () => navigate('/atlas'),
      },
      {
        id: 'nav-board',
        group: groupNav,
        label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
          label: t('v2.nav.board', 'Phase Board'),
        }),
        run: () => navigate('/board'),
      },
      ...(staffingNavCommand
        ? [
            {
              id: staffingNavCommand.id,
              group: groupNav,
              label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
                label: t(staffingNavCommand.labelKey, staffingNavCommand.fallbackLabel),
              }),
              run: () => navigate(staffingNavCommand.to),
            },
          ]
        : []),
      {
        id: 'nav-briefings',
        group: groupNav,
        label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
          label: t('v2.nav.briefings', 'Briefings'),
        }),
        run: () => navigate('/briefings'),
      },
      {
        id: 'nav-settings',
        group: groupNav,
        label: t('v2.cmdk.nav_open', '{{label}} öffnen', {
          label: t('v2.nav.settings', 'Einstellungen'),
        }),
        run: () => navigate('/einstellungen'),
      },
      {
        id: 'act-sync',
        group: groupAct,
        label: t('v2.cmdk.act_sync', 'Salesforce-Sync manuell starten'),
        hint: t('v2.cmdk.act_sync_hint', 'admin only'),
        run: () => navigate('/admin/sync'),
      },
      {
        id: 'set-theme-toggle',
        group: groupSet,
        label:
          theme === 'mindsquare'
            ? t('v2.cmdk.theme_to_mc', 'Theme: Mission Control aktivieren')
            : t('v2.cmdk.theme_to_ms', 'Theme: mindsquare aktivieren'),
        run: () => setTheme(theme === 'mindsquare' ? 'mission-control' : 'mindsquare'),
      },
      {
        id: 'set-locale-de',
        group: groupSet,
        label: t('v2.cmdk.locale_de', 'Sprache: Deutsch'),
        run: () => i18n.changeLanguage('de'),
      },
      {
        id: 'set-locale-en',
        group: groupSet,
        label: t('v2.cmdk.locale_en', 'Sprache: English'),
        run: () => i18n.changeLanguage('en'),
      },
    ],
    [navigate, theme, setTheme, i18n, t, groupNav, groupAct, groupSet, staffingNavCommand],
  );

  const projectCommands = useMemo<Command[]>(() => {
    const items = searchQuery.data?.items ?? [];
    return items.map((p) => ({
      id: `proj-${p.project_id}`,
      group: groupProjects,
      label: formatProjectSearchLabel(p),
      hint: p.segment,
      run: () => navigate(`/projects/${p.project_id}`),
    }));
  }, [searchQuery.data, groupProjects, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const staticFiltered = q ? commands.filter((c) => c.label.toLowerCase().includes(q)) : commands;
    // Projekt-Treffer zuerst (serverseitig gematcht, nicht nochmal filtern).
    return [...projectCommands, ...staticFiltered];
  }, [query, commands, projectCommands]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Pfeile + Enter. Escape behandelt <Modal> (Issue #67) — hier waere es doppelt.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        const cmd = filtered[highlight];
        if (cmd) {
          e.preventDefault();
          cmd.run();
          onClose();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, highlight, onClose]);

  if (!open) return null;

  // Group by section
  const groups = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    const arr = acc[c.group] ?? [];
    arr.push(c);
    acc[c.group] = arr;
    return acc;
  }, {});

  let runningIdx = 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Eigener Name statt des Platzhaltertexts im Suchfeld — der beschreibt, was man
      // tun kann, benennt aber den Dialog nicht.
      label={t('v2.cmdk.dialog_label', 'Befehlspalette')}
      panelClassName="w-[560px] max-w-[90vw] overflow-hidden"
    >
      <div
        className="flex items-center gap-2 px-4 py-3 border-b"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          style={{ color: 'var(--ink-3)' }}
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          placeholder={t('v2.cmdk.placeholder', 'Suchen, springen, ausführen…')}
          className="flex-1 bg-transparent outline-none text-base"
          style={{ color: 'var(--ink)' }}
        />
        <kbd
          className="px-1.5 py-0.5 rounded text-[10px]"
          style={{
            background: 'var(--surface-3)',
            color: 'var(--ink-2)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          ESC
        </kbd>
      </div>

      <div className="max-h-[60vh] overflow-y-auto py-2">
        {Object.entries(groups).map(([group, cmds]) => (
          <div key={group} className="mb-2">
            <div
              className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {group}
            </div>
            {cmds.map((cmd) => {
              const idx = runningIdx++;
              const active = idx === highlight;
              return (
                <button
                  key={cmd.id}
                  type="button"
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => {
                    cmd.run();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
                  style={{
                    background: active ? 'var(--surface-2)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--ink-2)',
                    borderLeft: active ? '2px solid var(--primary)' : '2px solid transparent',
                  }}
                >
                  {/* min-w-0 + truncate: die Trefferzeile bleibt einzeilig und wird
                        bei Platzmangel mit Ellipse gekürzt (#62). */}
                  <span className="flex-1 min-w-0 truncate">{cmd.label}</span>
                  {cmd.hint && (
                    <span
                      className="shrink-0 text-[10px]"
                      style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
                    >
                      {cmd.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.cmdk.no_match', 'Kein Treffer für „{{query}}"', { query })}
          </div>
        )}
      </div>
    </Modal>
  );
}
